import { useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../../context/appContext";
import {
	Box,
	Card,
	CircularProgress,
	CardContent,
	Stack,
	Typography,
	Grid2,
} from "@mui/material";
import {
	DashboardActiveBorrowersIcon,
	DashboardActiveCustomersIcon,
	DashboardAverageLoanSizeIcon,
	DashboardCaseLoadIcon,
	DashboardCostPerBorrowerIcon,
	DashboardIncomeToLiabilityRatioIcon,
	DashboardLoanRepaymentRateIcon,
	DashboardNetInterestMarginIcon,
	DashboardNumberOfGroupsIcon,
	DashboardNumberOfSaversIcon,
	DashboardOperatingExpenseRatioIcon,
	DashboardOutstandingLoanBalanceIcon,
	DashboardPar30Days,
	DashboardReturnOnAssets,
	DashboardSavingsMobilized,
	DashboardSavingsToLoanRatio,
	DashboardWriteOffRatio,
} from "../../assets";
import { AppLayout } from "../../containers/layout/app";
import { DashboardWrapper } from "./styled";
import { BaseLineGraph } from "../../components/chart/graph";
import { BaseDoughnutChart } from "../../components/chart/doughnut";
import { useQuery } from "@tanstack/react-query";
import Cookies from "universal-cookie";
import { retrieveDashboardOverviewService } from "../../utils/api/dashboard";
import { formatAmountDisplay } from "../../helpers/formatAmountDisplay";
import { retrieveUserProfileService } from "../../utils/api/usermanagement/profile/retrieveUser";
import { retrieveInstitutionByIdService } from "../../utils/outbound/institution/retrieveInstitutionById";
import { retrieveCurrencyByIdService } from "../../utils/outbound/currency/retrieveCurrencyById";
import { AccessControlType } from "../../types/app.type";
import { retrieveRoleByIdService } from "../../utils/outbound/role/retrieveRoleById";
import { retrieveRoleService } from "../../utils/api/usermanagement/role/retrieveRole";
import { retrieveAccessControlByIdService } from "../../utils/api/usermanagement/accesscontrol/retrieveAccessControlById";
import { LoginTable } from "../../containers/tables/logintable";

export const Dashboard = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const initialAccessControl = useMemo(
		() => [
			{ module: "Accounting", rights: [] },
			{ module: "Approval", rights: [] },
			{ module: "Configuration", rights: [] },
			{ module: "Customer Service", rights: [] },
			{ module: "Dashboard", rights: [] },
			{ module: "FTD Management", rights: [] },
			{ module: "Loan Management", rights: [] },
			{ module: "Report", rights: [] },
			{ module: "Teller Management", rights: [] },
			{ module: "Transactions", rights: [] },
			{ module: "User Management", rights: [] },
		],
		[],
	);
	const { setActiveNavItem } = useContext(AppContext);

	const [userRole, setUserRole] = useState({
		title: "",
		accessControl: "",
	});
	const [accessControl, setAccessControl] =
		useState<AccessControlType[]>(initialAccessControl);

	useEffect(() => {
		setActiveNavItem("Dashboard");
	}, [setActiveNavItem]);

	const { data, isLoading } = useQuery({
		queryKey: ["dashboardoverview", TOKEN],
		queryFn: () => retrieveDashboardOverviewService(TOKEN),
		enabled: !!TOKEN,
	});

	const { data: currency } = useQuery({
		queryKey: ["currencySymbol", TOKEN],
		queryFn: async () => {
			const user = await retrieveUserProfileService(TOKEN);
			const institution = await retrieveInstitutionByIdService(
				user?._id,
				user?.institution,
			);
			const currencyDetails = await retrieveCurrencyByIdService(
				user?._id,
				institution?.currency,
			);
			return currencyDetails?.symbol;
		},
		enabled: !!TOKEN,
	});

	const { data: loggedInUser } = useQuery({
		queryKey: ["loggedInUser", TOKEN],
		queryFn: async () => {
			return await retrieveUserProfileService(TOKEN);
		},
		enabled: !!TOKEN,
	});

	useEffect(() => {
		const fetchUserRole = async () => {
			if (!loggedInUser) return;
			const roleId = loggedInUser.roles[loggedInUser.roles.length - 1];
			try {
				const retrievedRole = await retrieveRoleByIdService(
					loggedInUser._id,
					roleId,
				);
				setUserRole({
					title: retrievedRole?.title,
					accessControl: retrievedRole?.accessControl,
				});
			} catch (error) {
				try {
					const retrievedRole = await retrieveRoleService(TOKEN, roleId);
					setUserRole({
						title: retrievedRole?.title,
						accessControl: retrievedRole?.accessControl,
					});
				} catch (error) {
					console.error("Role not found:", error);
				}
			}
		};
		fetchUserRole();
	}, [loggedInUser, TOKEN]);

	useEffect(() => {
		const fetchAccessControl = async (id: string) => {
			if (!id.trim()) {
				return setAccessControl(initialAccessControl);
			}
			try {
				const data = await retrieveAccessControlByIdService(TOKEN, id);
				// Transform the data to extract only the title values
				const transformedPermissions = data.permissions.map(
					(permission: Record<string, any>) => ({
						module: permission.module,
						rights: permission.rights.map(
							(right: Record<string, any>) => right.title,
						),
					}),
				);
				setAccessControl(
					transformedPermissions.length > 0
						? transformedPermissions
						: initialAccessControl,
				);
			} catch (err) {
				console.error("Failed to fetch access control:", err);
			}
		};
		fetchAccessControl(userRole?.accessControl);
	}, [TOKEN, initialAccessControl, userRole]);

	const getColor = (str: string) => {
		let hash = 0;
		for (let i = 0; i < str.length; i++) {
			hash = str.charCodeAt(i) + ((hash << 5) - hash);
		}
		const hue = hash % 360;
		return `hsl(${hue}, 65%, 60%)`;
	};

	const {
		activeCustomers,
		numberOfGroups,
		savingsMobilized,
		numberOfSavers,
		caseLoad,
		savingsToLoanRatio,
		activeBorrowers,
		netInterestMargin,
		costPerBorrower,
		loanRepaymentRate,
		returnOnAssets,
		incomeToLiabilityRatio,
		outstandingLoanBalance,
		par30Days,
		writeOffRatio,
		averageLoanSize,
		operatingExpenseRatio,
		borrowersByGender,
		disbursedLoanByProduct,
		totalLoanDisbursed,
		recentLogins,
	} = data || {};

	return (
		<AppLayout pageId={"Dashboard"} pageTitle={"Dashboard"}>
			<DashboardWrapper>
				<Stack
					direction={{ laptop: "row" }}
					gap={"var(--flex-gap)"}
					justifyContent={"space-between"}
				>
					{(userRole?.title === "MFI Admin" ||
						accessControl
							.find((item) => item.module === "Dashboard")
							?.rights.includes("Active Customers")) && (
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 1,
								border: "1px solid var(--border-diluted-violet-color)",
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"calc(var(--basic-padding) / 2)"}
									gap={"calc(var(--flex-gap) / 2)"}
									justifyContent={"space-between"}
								>
									<Box overflow={"hidden"}>
										<DashboardActiveCustomersIcon />
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="h3"
											fontFamily={"Poppins"}
											fontWeight={400}
											fontSize={16}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											Active Customers
										</Typography>
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="body1"
											fontFamily={"Poppins"}
											fontWeight={700}
											fontSize={20}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											{isLoading ? (
												<CircularProgress color="inherit" className="loader" />
											) : (
												`${activeCustomers ?? 0}`
											)}
										</Typography>
									</Box>
								</Stack>
							</CardContent>
						</Card>
					)}
					{(userRole?.title === "MFI Admin" ||
						accessControl
							.find((item) => item.module === "Dashboard")
							?.rights.includes("Number of Groups")) && (
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 1,
								border: "1px solid var(--border-diluted-violet-color)",
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"calc(var(--basic-padding) / 2)"}
									gap={"calc(var(--flex-gap) / 2)"}
									justifyContent={"space-between"}
								>
									<Box overflow={"hidden"}>
										<DashboardNumberOfGroupsIcon />
									</Box>
									<Box>
										<Typography
											variant="h3"
											fontFamily={"Poppins"}
											fontWeight={400}
											fontSize={16}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											Number of Groups
										</Typography>
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="body1"
											fontFamily={"Poppins"}
											fontWeight={700}
											fontSize={20}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											{isLoading ? (
												<CircularProgress color="inherit" className="loader" />
											) : (
												`${numberOfGroups ?? 0}`
											)}
										</Typography>
									</Box>
								</Stack>
							</CardContent>
						</Card>
					)}
					{(userRole?.title === "MFI Admin" ||
						accessControl
							.find((item) => item.module === "Dashboard")
							?.rights.includes("Savings Mobilized")) && (
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 1,
								border: "1px solid var(--border-diluted-violet-color)",
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"calc(var(--basic-padding) / 2)"}
									gap={"calc(var(--flex-gap) / 2)"}
									justifyContent={"space-between"}
								>
									<Box overflow={"hidden"}>
										<DashboardSavingsMobilized />
									</Box>
									<Box>
										<Typography
											variant="h3"
											fontFamily={"Poppins"}
											fontWeight={400}
											fontSize={16}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											Savings Mobilized
										</Typography>
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="body1"
											fontFamily={"Poppins"}
											fontWeight={700}
											fontSize={20}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											{isLoading ? (
												<CircularProgress color="inherit" className="loader" />
											) : (
												`${currency}${formatAmountDisplay(savingsMobilized ?? 0)}`
											)}
										</Typography>
									</Box>
								</Stack>
							</CardContent>
						</Card>
					)}
					{(userRole?.title === "MFI Admin" ||
						accessControl
							.find((item) => item.module === "Dashboard")
							?.rights.includes("Number of Savers")) && (
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 1,
								border: "1px solid var(--border-diluted-violet-color)",
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"calc(var(--basic-padding) / 2)"}
									gap={"calc(var(--flex-gap) / 2)"}
									justifyContent={"space-between"}
								>
									<Box overflow={"hidden"}>
										<DashboardNumberOfSaversIcon />
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="h3"
											fontFamily={"Poppins"}
											fontWeight={400}
											fontSize={16}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											Number of Savers
										</Typography>
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="body1"
											fontFamily={"Poppins"}
											fontWeight={700}
											fontSize={20}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											{isLoading ? (
												<CircularProgress color="inherit" className="loader" />
											) : (
												`${numberOfSavers ?? 0}`
											)}
										</Typography>
									</Box>
								</Stack>
							</CardContent>
						</Card>
					)}
				</Stack>
				<Stack
					direction={{ laptop: "row" }}
					gap={"var(--flex-gap)"}
					justifyContent={"space-between"}
				>
					{(userRole?.title === "MFI Admin" ||
						accessControl
							.find((item) => item.module === "Dashboard")
							?.rights.includes("Case Load")) && (
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 1,
								border: "1px solid var(--border-diluted-violet-color)",
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"calc(var(--basic-padding) / 2)"}
									gap={"calc(var(--flex-gap) / 2)"}
									justifyContent={"space-between"}
								>
									<Box overflow={"hidden"}>
										<DashboardCaseLoadIcon />
									</Box>
									<Box>
										<Typography
											variant="h3"
											fontFamily={"Poppins"}
											fontWeight={400}
											fontSize={16}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											Case Load
										</Typography>
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="body1"
											fontFamily={"Poppins"}
											fontWeight={700}
											fontSize={20}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											{isLoading ? (
												<CircularProgress color="inherit" className="loader" />
											) : (
												`${caseLoad ?? 0}`
											)}
										</Typography>
									</Box>
								</Stack>
							</CardContent>
						</Card>
					)}
					{(userRole?.title === "MFI Admin" ||
						accessControl
							.find((item) => item.module === "Dashboard")
							?.rights.includes("Savings to Loan Ratio")) && (
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 1,
								border: "1px solid var(--border-diluted-violet-color)",
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"calc(var(--basic-padding) / 2)"}
									gap={"calc(var(--flex-gap) / 2)"}
									justifyContent={"space-between"}
								>
									<Box overflow={"hidden"}>
										<DashboardSavingsToLoanRatio />
									</Box>
									<Box>
										<Typography
											variant="h3"
											fontFamily={"Poppins"}
											fontWeight={400}
											fontSize={16}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											Savings to Loan Ratio
										</Typography>
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="body1"
											fontFamily={"Poppins"}
											fontWeight={700}
											fontSize={20}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											{isLoading ? (
												<CircularProgress color="inherit" className="loader" />
											) : (
												`${savingsToLoanRatio ?? "0:0"}`
											)}
										</Typography>
									</Box>
								</Stack>
							</CardContent>
						</Card>
					)}
					{(userRole?.title === "MFI Admin" ||
						accessControl
							.find((item) => item.module === "Dashboard")
							?.rights.includes("Active Borrowers")) && (
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 1,
								border: "1px solid var(--border-diluted-violet-color)",
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"calc(var(--basic-padding) / 2)"}
									gap={"calc(var(--flex-gap) / 2)"}
									justifyContent={"space-between"}
								>
									<Box overflow={"hidden"}>
										<DashboardActiveBorrowersIcon />
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="h3"
											fontFamily={"Poppins"}
											fontWeight={400}
											fontSize={16}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											Active Borrowers
										</Typography>
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="body1"
											fontFamily={"Poppins"}
											fontWeight={700}
											fontSize={20}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											{isLoading ? (
												<CircularProgress color="inherit" className="loader" />
											) : (
												`${activeBorrowers ?? 0}`
											)}
										</Typography>
									</Box>
								</Stack>
							</CardContent>
						</Card>
					)}
					{(userRole?.title === "MFI Admin" ||
						accessControl
							.find((item) => item.module === "Dashboard")
							?.rights.includes("Net Interest Margin")) && (
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 1,
								border: "1px solid var(--border-diluted-violet-color)",
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"calc(var(--basic-padding) / 2)"}
									gap={"calc(var(--flex-gap) / 2)"}
									justifyContent={"space-between"}
								>
									<Box overflow={"hidden"}>
										<DashboardNetInterestMarginIcon />
									</Box>
									<Box>
										<Typography
											variant="h3"
											fontFamily={"Poppins"}
											fontWeight={400}
											fontSize={16}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											Net Interest Margin
										</Typography>
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="body1"
											fontFamily={"Poppins"}
											fontWeight={700}
											fontSize={20}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											{isLoading ? (
												<CircularProgress color="inherit" className="loader" />
											) : (
												`${netInterestMargin ?? 0}%`
											)}
										</Typography>
									</Box>
								</Stack>
							</CardContent>
						</Card>
					)}
				</Stack>
				<Stack
					direction={{ laptop: "row" }}
					gap={"var(--flex-gap)"}
					justifyContent={"space-between"}
				>
					{(userRole?.title === "MFI Admin" ||
						accessControl
							.find((item) => item.module === "Dashboard")
							?.rights.includes("Cost Per Borrower")) && (
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 1,
								border: "1px solid var(--border-diluted-violet-color)",
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"calc(var(--basic-padding) / 2)"}
									gap={"calc(var(--flex-gap) / 2)"}
									justifyContent={"space-between"}
								>
									<Box overflow={"hidden"}>
										<DashboardCostPerBorrowerIcon />
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="h3"
											fontFamily={"Poppins"}
											fontWeight={400}
											fontSize={16}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											Cost Per Borrower
										</Typography>
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="body1"
											fontFamily={"Poppins"}
											fontWeight={700}
											fontSize={20}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											{isLoading ? (
												<CircularProgress color="inherit" className="loader" />
											) : (
												`${currency}${formatAmountDisplay(costPerBorrower ?? 0)}`
											)}
										</Typography>
									</Box>
								</Stack>
							</CardContent>
						</Card>
					)}
					{(userRole?.title === "MFI Admin" ||
						accessControl
							.find((item) => item.module === "Dashboard")
							?.rights.includes("Loan Repayment Rate")) && (
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 1,
								border: "1px solid var(--border-diluted-violet-color)",
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"calc(var(--basic-padding) / 2)"}
									gap={"calc(var(--flex-gap) / 2)"}
									justifyContent={"space-between"}
								>
									<Box overflow={"hidden"}>
										<DashboardLoanRepaymentRateIcon />
									</Box>
									<Box>
										<Typography
											variant="h3"
											fontFamily={"Poppins"}
											fontWeight={400}
											fontSize={16}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											Loan Repayment Rate
										</Typography>
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="body1"
											fontFamily={"Poppins"}
											fontWeight={700}
											fontSize={20}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											{isLoading ? (
												<CircularProgress color="inherit" className="loader" />
											) : (
												`${loanRepaymentRate ?? 0}%`
											)}
										</Typography>
									</Box>
								</Stack>
							</CardContent>
						</Card>
					)}
					{(userRole?.title === "MFI Admin" ||
						accessControl
							.find((item) => item.module === "Dashboard")
							?.rights.includes("Return on Assets")) && (
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 1,
								border: "1px solid var(--border-diluted-violet-color)",
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"calc(var(--basic-padding) / 2)"}
									gap={"calc(var(--flex-gap) / 2)"}
									justifyContent={"space-between"}
								>
									<Box overflow={"hidden"}>
										<DashboardReturnOnAssets />
									</Box>
									<Box>
										<Typography
											variant="h3"
											fontFamily={"Poppins"}
											fontWeight={400}
											fontSize={16}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											Return on Assets (ROA)
										</Typography>
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="body1"
											fontFamily={"Poppins"}
											fontWeight={700}
											fontSize={20}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											{isLoading ? (
												<CircularProgress color="inherit" className="loader" />
											) : (
												`${returnOnAssets ?? 0}%`
											)}
										</Typography>
									</Box>
								</Stack>
							</CardContent>
						</Card>
					)}
					{(userRole?.title === "MFI Admin" ||
						accessControl
							.find((item) => item.module === "Dashboard")
							?.rights.includes("Income to Liability Ratio")) && (
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 1,
								border: "1px solid var(--border-diluted-violet-color)",
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"calc(var(--basic-padding) / 2)"}
									gap={"calc(var(--flex-gap) / 2)"}
									justifyContent={"space-between"}
								>
									<Box overflow={"hidden"}>
										<DashboardIncomeToLiabilityRatioIcon />
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="h3"
											fontFamily={"Poppins"}
											fontWeight={400}
											fontSize={16}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											Income to Liability Ratio
										</Typography>
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="body1"
											fontFamily={"Poppins"}
											fontWeight={700}
											fontSize={20}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											{isLoading ? (
												<CircularProgress color="inherit" className="loader" />
											) : (
												`${incomeToLiabilityRatio ?? "0:0"}`
											)}
										</Typography>
									</Box>
								</Stack>
							</CardContent>
						</Card>
					)}
				</Stack>
				<Stack
					gap={"var(--flex-gap)"}
					direction={{ laptop: "row" }}
					justifyContent={"space-between"}
				>
					{(userRole?.title === "MFI Admin" ||
						accessControl
							.find((item) => item.module === "Dashboard")
							?.rights.includes("Outstanding Loan Balance")) && (
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 1,
								border: "1px solid var(--border-diluted-violet-color)",
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"calc(var(--basic-padding) / 2)"}
									gap={"calc(var(--flex-gap) / 2)"}
									justifyContent={"space-between"}
								>
									<Box overflow={"hidden"}>
										<DashboardOutstandingLoanBalanceIcon />
									</Box>
									<Box>
										<Typography
											variant="h3"
											fontFamily={"Poppins"}
											fontWeight={400}
											fontSize={16}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											Outstanding Loan Balance
										</Typography>
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="body1"
											fontFamily={"Poppins"}
											fontWeight={700}
											fontSize={20}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											{isLoading ? (
												<CircularProgress color="inherit" className="loader" />
											) : (
												`${currency}${formatAmountDisplay(outstandingLoanBalance ?? 0)}`
											)}
										</Typography>
									</Box>
								</Stack>
							</CardContent>
						</Card>
					)}
					{(userRole?.title === "MFI Admin" ||
						accessControl
							.find((item) => item.module === "Dashboard")
							?.rights.includes("PAR 30 Days")) && (
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 1,
								border: "1px solid var(--border-diluted-violet-color)",
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"calc(var(--basic-padding) / 2)"}
									gap={"calc(var(--flex-gap) / 2)"}
									justifyContent={"space-between"}
								>
									<Box overflow={"hidden"}>
										<DashboardPar30Days />
									</Box>
									<Box>
										<Typography
											variant="h3"
											fontFamily={"Poppins"}
											fontWeight={400}
											fontSize={16}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											PAR 30 Days
										</Typography>
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="body1"
											fontFamily={"Poppins"}
											fontWeight={700}
											fontSize={20}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											{isLoading ? (
												<CircularProgress color="inherit" className="loader" />
											) : (
												`${par30Days ?? 0}%`
											)}
										</Typography>
									</Box>
								</Stack>
							</CardContent>
						</Card>
					)}
					{(userRole?.title === "MFI Admin" ||
						accessControl
							.find((item) => item.module === "Dashboard")
							?.rights.includes("Write Off Ratio")) && (
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 1,
								border: "1px solid var(--border-diluted-violet-color)",
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"calc(var(--basic-padding) / 2)"}
									gap={"calc(var(--flex-gap) / 2)"}
									justifyContent={"space-between"}
								>
									<Box overflow={"hidden"}>
										<DashboardWriteOffRatio />
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="h3"
											fontFamily={"Poppins"}
											fontWeight={400}
											fontSize={16}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											Write Off Ratio
										</Typography>
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="body1"
											fontFamily={"Poppins"}
											fontWeight={700}
											fontSize={20}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											{isLoading ? (
												<CircularProgress color="inherit" className="loader" />
											) : (
												`${writeOffRatio ?? "0:0"}`
											)}
										</Typography>
									</Box>
								</Stack>
							</CardContent>
						</Card>
					)}
					{(userRole?.title === "MFI Admin" ||
						accessControl
							.find((item) => item.module === "Dashboard")
							?.rights.includes("Average Loan Size")) && (
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 1,
								border: "1px solid var(--border-diluted-violet-color)",
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"calc(var(--basic-padding) / 2)"}
									gap={"calc(var(--flex-gap) / 2)"}
									justifyContent={"space-between"}
								>
									<Box overflow={"hidden"}>
										<DashboardAverageLoanSizeIcon />
									</Box>
									<Box>
										<Typography
											variant="h3"
											fontFamily={"Poppins"}
											fontWeight={400}
											fontSize={16}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											Average Loan Size
										</Typography>
									</Box>
									<Box overflow={"hidden"}>
										<Typography
											variant="body1"
											fontFamily={"Poppins"}
											fontWeight={700}
											fontSize={20}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											{isLoading ? (
												<CircularProgress color="inherit" className="loader" />
											) : (
												`${currency}${formatAmountDisplay(averageLoanSize ?? 0)}`
											)}
										</Typography>
									</Box>
								</Stack>
							</CardContent>
						</Card>
					)}
				</Stack>
				<Grid2
					container
					component={"div"}
					spacing={"var(--flex-gap)"}
					className="fieldset-grid-wrapper"
				>
					<Grid2 size={{ mobile: 12, laptop: 3 }}>
						<Stack gap={"var(--flex-gap)"} height={"-webkit-fill-available"}>
							{(userRole?.title === "MFI Admin" ||
								accessControl
									.find((item) => item.module === "Dashboard")
									?.rights.includes("Operating Expense Ratio")) && (
								<Card
									sx={{
										flex: "0 1 auto",
										borderRadius: "1rem",
										border: "1px solid var(--border-diluted-violet-color)",
									}}
								>
									<CardContent className="card-content">
										<Stack
											height={"100%"}
											padding={"calc(var(--basic-padding) / 2)"}
											gap={"calc(var(--flex-gap) / 2)"}
											justifyContent={"space-between"}
										>
											<Box overflow={"hidden"}>
												<DashboardOperatingExpenseRatioIcon />
											</Box>
											<Box>
												<Typography
													variant="h3"
													fontFamily={"Poppins"}
													fontWeight={400}
													fontSize={16}
													lineHeight={"normal"}
													color="var(--dark-color)"
												>
													Operating Expense Ratio
												</Typography>
											</Box>
											<Box overflow={"hidden"}>
												<Typography
													variant="body1"
													fontFamily={"Poppins"}
													fontWeight={700}
													fontSize={20}
													lineHeight={"normal"}
													color="var(--dark-color)"
												>
													{isLoading ? (
														<CircularProgress
															color="inherit"
															className="loader"
														/>
													) : (
														`${operatingExpenseRatio ?? "0:0"}`
													)}
												</Typography>
											</Box>
										</Stack>
									</CardContent>
								</Card>
							)}
							{(userRole?.title === "MFI Admin" ||
								accessControl
									.find((item) => item.module === "Dashboard")
									?.rights.includes("Borrowers by Gender")) && (
								<Card
									sx={{
										borderRadius: "1rem",
										flex: 1,
										border: "1px solid var(--border-diluted-violet-color)",
									}}
								>
									<CardContent className="card-content">
										<Box
											height={"100%"}
											padding={"calc(var(--basic-padding) / 2)"}
										>
											<BaseDoughnutChart
												title="Borrowers by Gender"
												cutout="50%"
												width="100%"
												height="120px"
												labels={["Male", "Female"]}
												datasets={[
													{
														label: "BBG",
														data: [
															borrowersByGender?.male ?? 0,
															borrowersByGender?.female ?? 0,
														],
														backgroundColor: ["#791377", "#BACEFF"],
													},
												]}
											/>
										</Box>
									</CardContent>
								</Card>
							)}
							{(userRole?.title === "MFI Admin" ||
								accessControl
									.find((item) => item.module === "Dashboard")
									?.rights.includes("Disbursed Loan by Product")) && (
								<Card
									sx={{
										borderRadius: "1rem",
										flex: 1,
										border: "1px solid var(--border-diluted-violet-color)",
									}}
								>
									<CardContent className="card-content">
										<Box
											height={"100%"}
											padding={"calc(var(--basic-padding) / 2)"}
										>
											<BaseDoughnutChart
												title="Disbursed Loan by Product"
												cutout="50%"
												width="100%"
												height="120px"
												labels={Object.keys(disbursedLoanByProduct || {})}
												datasets={[
													{
														label: "DLP",
														data: Object.values(disbursedLoanByProduct || {}),
														backgroundColor: Object.keys(
															disbursedLoanByProduct || {},
														).map((label) => getColor(label)),
													},
												]}
											/>
										</Box>
									</CardContent>
								</Card>
							)}
						</Stack>
					</Grid2>
					<Grid2 size={{ mobile: 12, laptop: 9 }}>
						<Stack gap={"var(--flex-gap)"}>
							{(userRole?.title === "MFI Admin" ||
								accessControl
									.find((item) => item.module === "Dashboard")
									?.rights.includes("Total Loan Disbursed")) && (
								<Card
									sx={{
										flex: 1,
										borderRadius: "1rem",
										border: "1px solid var(--border-diluted-violet-color)",
									}}
								>
									<CardContent className="card-content">
										<Box
											height={"100%"}
											padding={"calc(var(--basic-padding) / 2)"}
										>
											<BaseLineGraph
												title="Total Loan Disbursed"
												width="100%"
												height="399px"
												labels={Object.keys(totalLoanDisbursed || {}).map(
													(label) =>
														label.charAt(0).toUpperCase() + label.slice(1),
												)}
												datasets={[
													{
														label: "TLD",
														data: Object.values(totalLoanDisbursed || {}),
														backgroundColor: "#791377",
														borderColor: "#791377",
													},
												]}
											/>
										</Box>
									</CardContent>
								</Card>
							)}
							{(userRole?.title === "MFI Admin" ||
								accessControl
									.find((item) => item.module === "Dashboard")
									?.rights.includes("Recent Logins")) && (
								<Box component={"div"} className="login-table-box">
									<LoginTable rows={recentLogins} />
								</Box>
							)}
						</Stack>
					</Grid2>
				</Grid2>
			</DashboardWrapper>
		</AppLayout>
	);
};
